import React, { useEffect, useState } from 'react';
import { useAuth } from '../AuthContext';
import {
  getNotificationSettings, saveNotificationSettings, sendTestNotification,
  getSchedule, saveSchedule, runScheduleNow,
  getAppSettings, saveAppSettings, getLoginLog,
} from '../api';
import { User, UserPlus, Bell, CalendarClock, Send, Play, Save, ShieldCheck, CheckCircle2, XCircle, Clock } from 'lucide-react';
import toast from 'react-hot-toast';
import Button from '../components/Button';
import TwoFactorCard from '../components/TwoFactorCard';
import { IDLE_KEY, getIdleMinutes } from '../components/IdleLogout';

const DAYS = ['Sunday','Monday','Tuesday','Wednesday','Thursday','Friday','Saturday'];

// Auto sign-out choices. 0 = off; the rest are minutes of inactivity.
const IDLE_CHOICES = [
  { value: 0,  label: 'Off' },
  { value: 15, label: '15 minutes' },
  { value: 30, label: '30 minutes' },
  { value: 60, label: '1 hour' },
];

/* The auto sign-out length is a per-browser preference, so it is stored in
   this browser rather than on the server, and read by the idle guard. */
function AutoLogoutCard() {
  const [mins, setMins] = useState(getIdleMinutes);

  const choose = (value) => {
    setMins(value);
    try { localStorage.setItem(IDLE_KEY, String(value)); } catch { /* private mode */ }
    window.dispatchEvent(new Event('wpm:idle-settings')); // tell the guard right away
    toast.success(value ? `Auto sign-out set to ${IDLE_CHOICES.find(c => c.value === value)?.label}` : 'Auto sign-out turned off');
  };

  return (
    <div className="card">
      <div className="card-head"><h3><Clock size={14}/> Auto sign-out</h3></div>
      <div style={{ padding:'14px 18px' }}>
        <p style={{ fontSize:12.5, color:'var(--text2)', margin:'0 0 12px', lineHeight:1.55 }}>
          Sign out automatically after a stretch with no activity, so the dashboard isn’t left
          open on an unattended screen. A warning appears one minute before, with a chance to stay.
        </p>
        <div className="field" style={{ margin:0, maxWidth:220 }}>
          <label htmlFor="idle">Sign out after</label>
          <select id="idle" value={mins} onChange={e => choose(parseInt(e.target.value, 10))}>
            {IDLE_CHOICES.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
          </select>
        </div>
        <p style={{ fontSize:11, color:'var(--text3)', margin:'10px 0 0' }}>
          This choice is remembered in this browser.
        </p>
      </div>
    </div>
  );
}

function Toggle({ on, onClick }) {
  return <button type="button" className={`switch${on ? ' on' : ''}`} onClick={onClick} aria-pressed={on} />;
}

function Row({ label, sub, on, onToggle }) {
  return (
    <div className="setting-row">
      <div>
        <div className="sr-label">{label}</div>
        {sub && <div className="sr-sub">{sub}</div>}
      </div>
      <Toggle on={on} onClick={onToggle} />
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const [ns, setNs]   = useState(null);   // notification settings
  const [sc, setSc]   = useState(null);   // schedule
  const [app, setApp] = useState(null);   // app settings (registration)
  const [logins, setLogins] = useState(null); // recent sign-in activity
  const [savingNs, setSavingNs] = useState(false);
  const [savingSc, setSavingSc] = useState(false);
  const [running, setRunning]   = useState(false);

  useEffect(() => {
    getNotificationSettings().then(setNs).catch(console.error);
    getSchedule().then(setSc).catch(console.error);
    getAppSettings().then(setApp).catch(console.error);
    getLoginLog().then(setLogins).catch(() => setLogins([]));
  }, []);

  const toggleRegistration = async () => {
    const next = !app.allow_registration;
    setApp({ ...app, allow_registration: next }); // optimistic
    try { setApp(await saveAppSettings({ allow_registration: next })); toast.success(next ? 'Registrations enabled' : 'Registrations disabled'); }
    catch { toast.error('Save failed'); setApp({ ...app, allow_registration: !next }); }
  };

  const setN = (patch) => setNs(prev => ({ ...prev, ...patch }));
  const setS = (patch) => setSc(prev => ({ ...prev, ...patch }));

  const saveNotifs = async () => {
    setSavingNs(true);
    try { setNs(await saveNotificationSettings(ns)); toast.success('Notification settings saved'); }
    catch { toast.error('Save failed'); }
    finally { setSavingNs(false); }
  };

  const test = async () => {
    try { await sendTestNotification(); toast.success('Test notification sent!'); }
    catch { toast.error('Could not send test'); }
  };

  const saveSched = async () => {
    setSavingSc(true);
    try { setSc(await saveSchedule(sc)); toast.success('Schedule saved'); }
    catch { toast.error('Save failed'); }
    finally { setSavingSc(false); }
  };

  const runNow = async () => {
    setRunning(true);
    try {
      const r = await runScheduleNow();
      toast.success(`Done — ${r.updated} updated${r.failed ? `, ${r.failed} failed` : ''} across ${r.sites} site(s)`);
      getSchedule().then(setSc);
    } catch { toast.error('Run failed'); }
    finally { setRunning(false); }
  };

  return (
    <>
      <div className="page-header"><div><h1>Settings</h1><p>Account, notifications & automation</p></div></div>
      <div className="page-content">
        <div className="two-col">
          {/* ── LEFT ── */}
          <div>
            {/* Account */}
            <div className="card">
              <div className="card-head"><h3><User size={14}/> Account</h3></div>
              <div style={{padding:'14px 18px'}}>
                <div className="field"><label>Full name</label><input type="text" defaultValue={user?.name}/></div>
                <div className="field"><label>Email</label><input type="email" defaultValue={user?.email}/></div>
                <button className="btn primary" onClick={()=>toast.success('Saved!')}>Save changes</button>
              </div>
            </div>

            {/* Notifications */}
            <div className="card">
              <div className="card-head">
                <h3><Bell size={14}/> Notifications</h3>
                <Button className="btn sm" onClick={test} disabled={!ns}><Send size={12}/> Send test</Button>
              </div>
              <div style={{padding:'6px 18px 16px'}}>
                {!ns ? <div style={{padding:'14px 0',color:'var(--text3)',fontSize:13}}>Loading…</div> : (
                  <>
                    <Row label="Email alerts" sub="Get notified by email" on={ns.email_enabled} onToggle={()=>setN({email_enabled:!ns.email_enabled})} />
                    {ns.email_enabled && (
                      <div className="field" style={{margin:'10px 0 4px'}}>
                        <label>Send emails to</label>
                        <input type="email" value={ns.email_to || ''} placeholder="you@example.com" onChange={e=>setN({email_to:e.target.value})}/>
                      </div>
                    )}

                    <Row label="Telegram alerts" sub="Get notified on Telegram" on={ns.telegram_enabled} onToggle={()=>setN({telegram_enabled:!ns.telegram_enabled})} />
                    {ns.telegram_enabled && (
                      <div style={{margin:'10px 0 4px'}}>
                        <div className="field" style={{marginBottom:8}}>
                          <label>Telegram Chat ID</label>
                          <input type="text" value={ns.telegram_chat_id || ''} placeholder="e.g. 123456789" onChange={e=>setN({telegram_chat_id:e.target.value})}/>
                        </div>
                        <div className="field" style={{margin:0}}>
                          <label>Bot token <span style={{color:'var(--text3)'}}>(optional — uses server bot if blank)</span></label>
                          <input type="text" value={ns.telegram_bot_token || ''} placeholder="123456:ABC-DEF…" onChange={e=>setN({telegram_bot_token:e.target.value})}/>
                        </div>
                      </div>
                    )}

                    <div style={{fontSize:11,textTransform:'uppercase',letterSpacing:'.5px',color:'var(--text3)',fontWeight:600,margin:'14px 0 2px'}}>Alert me about</div>
                    <Row label="Site goes down / recovers" on={ns.on_site_down} onToggle={()=>setN({on_site_down:!ns.on_site_down})} />
                    <Row label="New updates available" on={ns.on_update_available} onToggle={()=>setN({on_update_available:!ns.on_update_available})} />
                    <Row label="Update / deploy results" on={ns.on_update_result} onToggle={()=>setN({on_update_result:!ns.on_update_result})} />

                    <Button className="btn primary" style={{marginTop:14}} onClick={saveNotifs} loading={savingNs}>
                      <Save size={13}/> {savingNs?'Saving…':'Save notifications'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>

          {/* ── RIGHT ── */}
          <div>
            {/* Access control */}
            <div className="card">
              <div className="card-head"><h3><UserPlus size={14}/> Access control</h3></div>
              <div style={{padding:'6px 18px 14px'}}>
                {!app ? <div style={{padding:'14px 0',color:'var(--text3)',fontSize:13}}>Loading…</div> : (
                  <Row
                    label="Allow new registrations"
                    sub={app.allow_registration ? 'Anyone can create an account' : 'Sign-ups are closed — only existing users can log in'}
                    on={app.allow_registration}
                    onToggle={toggleRegistration}
                  />
                )}
              </div>
            </div>

            {/* Two-factor authentication */}
            <TwoFactorCard />

            {/* Auto sign-out on inactivity */}
            <AutoLogoutCard />

            {/* Recent sign-in activity */}
            <div className="card">
              <div className="card-head">
                <h3><ShieldCheck size={14}/> Recent sign-in activity</h3>
                <span style={{fontSize:11,color:'var(--text3)'}}>last 20</span>
              </div>
              <div style={{padding:'4px 0'}}>
                {!logins ? (
                  <div style={{padding:'14px 18px',color:'var(--text3)',fontSize:13}}>Loading…</div>
                ) : logins.length === 0 ? (
                  <div style={{padding:'14px 18px',color:'var(--text3)',fontSize:13}}>No sign-in attempts recorded yet.</div>
                ) : logins.map((l, i) => (
                  <div key={i} style={{display:'flex',alignItems:'center',gap:10,padding:'9px 18px',borderBottom:i<logins.length-1?'1px solid var(--border)':'none'}}>
                    {l.success
                      ? <CheckCircle2 size={15} color="var(--green)" style={{flexShrink:0}}/>
                      : <XCircle size={15} color="var(--red)" style={{flexShrink:0}}/>}
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontSize:12.5,color:'var(--text)',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{l.email || 'unknown'}</div>
                      <div style={{fontSize:11,color:'var(--text3)'}}>{l.ip || '—'} · {new Date(l.created_at).toLocaleString()}</div>
                    </div>
                    <span className={`badge ${l.success ? 'green' : l.reason === 'locked' ? 'red' : 'amber'}`} style={{flexShrink:0}}>
                      {l.success ? 'success' : l.reason === 'locked' ? 'blocked' : 'failed'}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Auto-update schedule */}
            <div className="card">
              <div className="card-head">
                <h3><CalendarClock size={14}/> Auto-update schedule</h3>
                <Button className="btn sm" onClick={runNow} loading={running} disabled={!sc}><Play size={12}/> {running?'Running…':'Run now'}</Button>
              </div>
              <div style={{padding:'6px 18px 16px'}}>
                {!sc ? <div style={{padding:'14px 0',color:'var(--text3)',fontSize:13}}>Loading…</div> : (
                  <>
                    <Row label="Enable scheduled updates" sub={sc.last_run ? `Last run: ${new Date(sc.last_run).toLocaleString()}` : 'Never run yet'} on={sc.enabled} onToggle={()=>setS({enabled:!sc.enabled})} />

                    <div style={{display:'grid',gridTemplateColumns: sc.frequency==='weekly'?'1fr 1fr 1fr':'1fr 1fr',gap:8,margin:'12px 0'}}>
                      <div className="field" style={{margin:0}}>
                        <label>Frequency</label>
                        <select value={sc.frequency} onChange={e=>setS({frequency:e.target.value})}>
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                        </select>
                      </div>
                      {sc.frequency==='weekly' && (
                        <div className="field" style={{margin:0}}>
                          <label>Day</label>
                          <select value={sc.day_of_week} onChange={e=>setS({day_of_week:parseInt(e.target.value,10)})}>
                            {DAYS.map((d,i)=><option key={i} value={i}>{d}</option>)}
                          </select>
                        </div>
                      )}
                      <div className="field" style={{margin:0}}>
                        <label>Time (server)</label>
                        <select value={sc.run_hour} onChange={e=>setS({run_hour:parseInt(e.target.value,10)})}>
                          {Array.from({length:24},(_,h)=><option key={h} value={h}>{String(h).padStart(2,'0')}:00</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{fontSize:11,textTransform:'uppercase',letterSpacing:'.5px',color:'var(--text3)',fontWeight:600,margin:'4px 0 2px'}}>What to update</div>
                    <Row label="Plugins" on={sc.update_plugins} onToggle={()=>setS({update_plugins:!sc.update_plugins})} />
                    <Row label="Themes" on={sc.update_themes} onToggle={()=>setS({update_themes:!sc.update_themes})} />
                    <Row label="WordPress core" sub="Higher risk — off by default" on={sc.update_core} onToggle={()=>setS({update_core:!sc.update_core})} />
                    <Row label="Backup before updating" sub="Requires UpdraftPlus on each site" on={sc.backup_before} onToggle={()=>setS({backup_before:!sc.backup_before})} />

                    <Button className="btn primary" style={{marginTop:14}} onClick={saveSched} loading={savingSc}>
                      <Save size={13}/> {savingSc?'Saving…':'Save schedule'}
                    </Button>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}
